import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  Stack,
  Alert,
  Tooltip,
  Divider,
  Chip,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ReplyIcon from "@mui/icons-material/Reply";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import DocumentsList from "../../../components/layout/DocumentsList";
import { Button as ListButton } from "../../../components/ui/button";
import AdminPageHeader from "../../../components/common/AdminPageHeader";
import { Headset } from "lucide-react";

import {
  createTicket,
  getTickets,
  getTicketDetails,
  sendTicketReply,
} from "../../../services/ticketsService";
import { getLookupData } from "../../../services/lookupService";

function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [error, setError] = useState("");
  const [listError, setListError] = useState("");
  const [success, setSuccess] = useState("");

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [activeTicketId, setActiveTicketId] = useState(null);
  const [activeTicket, setActiveTicket] = useState(null);

  const [replyMessage, setReplyMessage] = useState("");
  const [replyTo, setReplyTo] = useState(null);

  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");

  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  const [ticketPriorities, setTicketPriorities] = useState([]);
  const [ticketCategories, setTicketCategories] = useState([]);
  const [ticketSources, setTicketSources] = useState([]);

  const [newTicket, setNewTicket] = useState({
    title: "",
    description: "",
    enum_ticket_priority_id: "",
    enum_ticket_source_id: "",
    lt_ticket_category_id: "",
    remarks: "",
  });

  const getFirstValue = useCallback((...values) => {
    return values.find(
      (value) =>
        value !== undefined && value !== null && String(value).trim() !== "",
    );
  }, []);

  const truncateText = useCallback((text = "", maxLength = 60) => {
    if (!text) return "";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  }, []);
  const isCustomerReply = useCallback((reply) => {
    const isCustomerValue = String(reply?.IS_CUSTOMER ?? "")
      .trim()
      .toLowerCase();

    const userTypeValue = String(reply?.USER_TYPE ?? "")
      .trim()
      .toLowerCase();

    const senderValue = String(reply?.sender ?? "")
      .trim()
      .toLowerCase();

    if (userTypeValue.includes("support") || userTypeValue.includes("agent")) {
      return false;
    }

    return (
      isCustomerValue === "true" ||
      isCustomerValue === "1" ||
      isCustomerValue === "y" ||
      isCustomerValue === "yes" ||
      userTypeValue.includes("customer") ||
      userTypeValue.includes("user") ||
      senderValue === "user"
    );
  }, []);

  const getReplyId = useCallback((reply) => {
    return reply?.TICKET_COMMENT_ID || reply?.COMMENT_ID || reply?.id || null;
  }, []);

  const getReplyDate = useCallback((reply) => {
    return reply?.COMMENT_DATE || reply?.CREATED_AT || reply?.createdAt || null;
  }, []);

  const getSenderLabel = useCallback((replyOrSender) => {
    if (typeof replyOrSender === "string") {
      return replyOrSender === "User" ? "You" : "Support Agent";
    }

    const isCustomerValue = String(replyOrSender?.IS_CUSTOMER ?? "")
      .trim()
      .toLowerCase();

    const userTypeValue = String(replyOrSender?.USER_TYPE ?? "")
      .trim()
      .toLowerCase();

    const senderValue = String(replyOrSender?.sender ?? "")
      .trim()
      .toLowerCase();

    const isCustomer =
      isCustomerValue === "true" ||
      isCustomerValue === "1" ||
      isCustomerValue === "y" ||
      isCustomerValue === "yes" ||
      userTypeValue === "customer" ||
      userTypeValue === "user" ||
      senderValue === "user";

    return isCustomer ? "You" : "Support Agent";
  }, []);

  const isImageAttachment = useCallback((attachment) => {
    if (!attachment) return false;

    const type = attachment.type || attachment.mimeType || "";
    const url = attachment.url || "";

    if (type.startsWith("image/")) return true;

    return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(url);
  }, []);

  const clearSelectedFile = useCallback(() => {
    if (filePreview) {
      URL.revokeObjectURL(filePreview);
    }

    setFile(null);
    setFilePreview("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [filePreview]);

  const normalizeLookupItems = useCallback(
    (response) => {
      const data = response?.data ?? response;

      const items = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.result)
            ? data.result
            : Array.isArray(data?.rows)
              ? data.rows
              : Array.isArray(data?.items)
                ? data.items
                : [];

      return items
        .map((item) => {
          const id = getFirstValue(
            item?.ENUM_TICKET_PRIORITY_ID,
            item?.ENUM_TICKET_SOURCE_ID,
            item?.LT_CATEGORY_ID,
          );

          const label = getFirstValue(
            item?.PRIORITY_NAME,
            item?.SOURCE_NAME,
            item?.CATEGORY_NAME,
          );

          return {
            id: String(id ?? ""),
            label: String(label ?? ""),
          };
        })
        .filter((item) => item.id && item.label);
    },
    [getFirstValue],
  );

  const loadLookups = useCallback(async () => {
    try {
      setLookupLoading(true);

      const [prioritiesResponse, sourcesResponse, categoriesResponse] =
        await Promise.all([
          getLookupData("TICKET_PRIORITY"),
          getLookupData("TICKET_SOURCE"),
          getLookupData("TICKET_CATEGORY"),
        ]);

      const priorities = normalizeLookupItems(prioritiesResponse);
      const sources = normalizeLookupItems(sourcesResponse);
      const categories = normalizeLookupItems(categoriesResponse);

      setTicketPriorities(priorities);
      setTicketSources(sources);
      setTicketCategories(categories);

      setNewTicket((previous) => ({
        ...previous,
        enum_ticket_priority_id:
          previous.enum_ticket_priority_id || priorities[0]?.id || "",
        enum_ticket_source_id:
          previous.enum_ticket_source_id || sources[0]?.id || "",
        lt_ticket_category_id:
          previous.lt_ticket_category_id || categories[0]?.id || "",
      }));
    } catch (err) {
      console.error("Failed to fetch ticket lookups:", err);
      setError("Failed to load ticket options.");
    } finally {
      setLookupLoading(false);
    }
  }, [normalizeLookupItems]);

  const loadTickets = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setListError("");

      const data = await getTickets();

      const formattedData = Array.isArray(data)
        ? data.map((item) => {
            const replies = Array.isArray(item.replies) ? item.replies : [];
            const lastReply = replies[replies.length - 1];

            let lastMessageText = "";

            if (lastReply) {
              const senderLabel = lastReply.sender === "User" ? "You" : "Agent";

              const attachmentName =
                lastReply.attachment?.name ||
                lastReply.attachment?.fileName ||
                "Attachment";

              const messageText =
                lastReply.message?.trim() || `📎 ${attachmentName}`;

              lastMessageText = `${senderLabel}: ${truncateText(messageText, 60)}`;
            } else {
              lastMessageText = truncateText(
                item.COMMENT_TEXT || item.DESCRIPTION || "",
                60,
              );
            }

            return {
              id: item.TICKET_ID,
              ticketNumber: item.TICKET_NUMBER,
              title: item.TITLE,
              category: item.CATEGORY_NAME || "General",
              priority: item.PRIORITY_NAME || "Medium",
              lastMessage: lastMessageText,
              status: item.STATUS_NAME || "Open",
              date: item.CREATED_AT
                ? new Date(item.CREATED_AT).toLocaleDateString()
                : item.CREATED_DATE
                  ? new Date(item.CREATED_DATE).toLocaleDateString()
                  : "-",
            };
          })
        : [];

      setTickets(formattedData);
    } catch (err) {
      console.error("Failed to fetch tickets:", err);
      setListError("Failed to fetch tickets list.");
    } finally {
      setLoading(false);
    }
  }, [truncateText]);

  const initializeData = useCallback(async () => {
    await Promise.all([loadLookups(), loadTickets()]);
  }, [loadLookups, loadTickets]);

  useEffect(() => {
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeTicketId) return;

    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [activeTicketId, activeTicket]);

  const handleSelectTicket = useCallback(
    async (selectedRow) => {
      const ticketId = selectedRow?.id;
      if (!ticketId) return;

      try {
        setLoading(true);
        setError("");

        const details = await getTicketDetails(ticketId);

        setActiveTicket(details);
        setActiveTicketId(ticketId);
        setReplyTo(null);
        setReplyMessage("");
        clearSelectedFile();
      } catch (err) {
        console.error("Could not load ticket details:", err);
        setError("Could not load ticket details.");
      } finally {
        setLoading(false);
      }
    },
    [clearSelectedFile],
  );

  const handleBackToList = useCallback(() => {
    setActiveTicketId(null);
    setActiveTicket(null);
    setReplyTo(null);
    setReplyMessage("");
    clearSelectedFile();
    loadTickets();
  }, [clearSelectedFile, loadTickets]);

  const handleFileChange = useCallback((event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    const maxFileSize = 10 * 1024 * 1024;

    if (selectedFile.size > maxFileSize) {
      setError("File size must be less than 10 MB.");
      event.target.value = "";
      return;
    }

    setError("");
    setFile(selectedFile);

    if (selectedFile.type.startsWith("image/")) {
      const previewUrl = URL.createObjectURL(selectedFile);
      setFilePreview(previewUrl);
    } else {
      setFilePreview("");
    }
  }, []);

  const handleSetReplyTo = useCallback((reply) => {
    setReplyTo(reply);
    setTimeout(() => {
      const input = document.getElementById("ticket-reply-input");
      input?.focus();
    }, 100);
  }, []);

  const handleSendReply = useCallback(
    async (event) => {
      event.preventDefault();

      const hasMessage = Boolean(replyMessage.trim());
      const hasFile = Boolean(file);

      if (!hasMessage && !hasFile) return;
      if (!activeTicketId) return;

      try {
        setActionLoading(true);
        setError("");

        const attachmentPayload = file
          ? {
              ...file,
              preview: filePreview,
            }
          : null;

        await sendTicketReply(
          activeTicketId,
          replyMessage.trim(),
          replyTo ? getReplyId(replyTo) : null,
          attachmentPayload,
        );

        setReplyMessage("");
        setReplyTo(null);
        clearSelectedFile();

        const updatedTicket = await getTicketDetails(activeTicketId);
        setActiveTicket(updatedTicket);

        loadTickets();
      } catch (err) {
        console.error("Failed to send reply:", err);
        setError(err?.message || "Failed to send message.");
      } finally {
        setActionLoading(false);
      }
    },
    [
      activeTicketId,
      clearSelectedFile,
      file,
      filePreview,
      getReplyId,
      loadTickets,
      replyMessage,
      replyTo,
    ],
  );

  const handleReplyKeyDown = useCallback(
    (event) => {
      if (event.key !== "Enter" || event.shiftKey) return;

      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
    },
    [],
  );

  const handleCreateSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      if (!newTicket.title.trim() || !newTicket.description.trim()) {
        setError("Title and Description are required.");
        return;
      }

      if (
        !newTicket.enum_ticket_priority_id ||
        !newTicket.enum_ticket_source_id ||
        !newTicket.lt_ticket_category_id
      ) {
        setError("Ticket options are not loaded yet.");
        return;
      }

      try {
        setActionLoading(true);
        setError("");
        setSuccess("");

        const result = await createTicket(newTicket);

        // استخراج امن TICKET_ID از پاسخ‌های مختلف
        const createdTicketId =
          result?.TICKET_ID ||
          result?.data?.TICKET_ID ||
          result?.RQ?.[0]?.TICKET_ID ||
          result?.RQS?.[0]?.TICKET_ID;

        if (!createdTicketId || String(createdTicketId) === "undefined") {
          console.error("createTicket raw response:", result);
          throw new Error("Ticket created but TICKET_ID was not returned.");
        }

        setSuccess("Ticket created successfully.");
        setOpenCreateDialog(false);

        setNewTicket({
          title: "",
          description: "",
          enum_ticket_priority_id: ticketPriorities[0]?.id || "",
          enum_ticket_source_id: ticketSources[0]?.id || "",
          lt_ticket_category_id: ticketCategories[0]?.id || "",
          remarks: "",
        });

        await loadTickets();

        const details = await getTicketDetails(createdTicketId);

        setActiveTicket(details);
        setActiveTicketId(createdTicketId);
      } catch (err) {
        console.error("Failed to create ticket:", err);
        setError(err?.message || "Failed to create ticket.");
      } finally {
        setActionLoading(false);
      }
    },
    [loadTickets, newTicket, ticketCategories, ticketPriorities, ticketSources],
  );

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
      }}
    >
      <Stack spacing={3}>
        <AdminPageHeader
          icon={Headset}
          title="Support Tickets"
          description="Manage your support tickets and track resolutions."
        />
        {error && (
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" onClose={() => setSuccess("")}>
            {success}
          </Alert>
        )}

        {activeTicketId && activeTicket ? (
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: 1120,
              mx: "auto",
              borderRadius: "var(--admin-radius)",
              border: "1px solid var(--surface-border)",
              backgroundColor: "var(--card-bg)",
              boxShadow: "var(--shadowx)",
              display: "flex",
              flexDirection: "column",
              height: { xs: "calc(100dvh - 210px)", md: "min(720px, 72vh)" },
              minHeight: { xs: 520, md: 580 },
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: { xs: 1.5, sm: 2.5 },
                py: 1.75,
                borderBottom: "1px solid var(--surface-border)",
                backgroundColor: "var(--surface)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
                <IconButton
                  onClick={handleBackToList}
                  aria-label="Back to tickets list"
                  sx={{
                    width: 40,
                    height: 40,
                    border: "1px solid var(--surface-border)",
                    color: "var(--text-secondary)",
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    color="var(--text-primary)"
                    noWrap
                  >
                    {activeTicket.ticket?.TITLE ||
                      activeTicket.title ||
                      "Ticket"}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <Typography variant="caption" color="var(--text-secondary)">
                      Ticket #{activeTicket.ticket?.TICKET_ID || activeTicket.id}
                    </Typography>

                    <Chip
                      label={
                        activeTicket.ticket?.STATUS_NAME ||
                        activeTicket.status ||
                        "Open"
                      }
                      size="small"
                      color={
                        (activeTicket.ticket?.STATUS_NAME ||
                          activeTicket.status) === "Resolved"
                          ? "success"
                          : "primary"
                      }
                      variant="outlined"
                      sx={{ height: 24, fontWeight: 700, fontSize: 12 }}
                    />
                  </Stack>
                </Box>
              </Stack>
            </Box>

            <Box
              sx={{
                flexGrow: 1,
                p: { xs: 2, sm: 3 },
                overflowY: "auto",
                backgroundColor: "var(--surface-muted)",
                minHeight: 0,
                scrollbarGutter: "stable",
              }}
            >
              <Stack spacing={2.25} sx={{ width: "100%", maxWidth: 880, mx: "auto" }}>
                {(activeTicket.comments || activeTicket.replies || []).length === 0 && (
                  <Box sx={{ py: 8, textAlign: "center" }}>
                    <Typography fontWeight={700} color="var(--text-primary)">
                      No messages yet
                    </Typography>
                    <Typography variant="body2" color="var(--text-secondary)" sx={{ mt: 0.5 }}>
                      Start the conversation by writing a reply below.
                    </Typography>
                  </Box>
                )}
                {(activeTicket.comments || activeTicket.replies || []).map(
                  (reply, index) => {
                    const isCustomer = isCustomerReply(reply);
                    const isAgent = !isCustomer;

                    const parentReply = reply.PARENT_COMMENT_ID
                      ? (
                          activeTicket.comments ||
                          activeTicket.replies ||
                          []
                        ).find(
                          (item) =>
                            String(getReplyId(item)) ===
                            String(reply.PARENT_COMMENT_ID),
                        )
                      : null;

                    const attachment = reply.ATTACHMENT || reply.attachment;

                    return (
                      <Box
                        key={getReplyId(reply) || index}
                        sx={{
                          alignSelf: isAgent ? "flex-start" : "flex-end",
                          width: "fit-content",
                          minWidth: 0,
                          maxWidth: { xs: "88%", sm: "72%" },
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            px: 1.75,
                            py: 1.25,
                            borderRadius: isAgent
                              ? "16px 16px 16px 4px"
                              : "16px 16px 4px 16px",
                            backgroundColor: isAgent
                              ? "var(--surface)"
                              : "var(--primary)",
                            color: isAgent ? "var(--text-primary)" : "#ffffff",
                            border: isAgent
                              ? "1px solid var(--surface-border)"
                              : "none",
                            boxShadow: isAgent
                              ? "0 1px 2px rgba(15, 23, 42, 0.04)"
                              : "0 6px 16px color-mix(in srgb, var(--primary) 18%, transparent)",
                          }}
                        >
                          {parentReply && (
                            <Box
                              sx={{
                                mb: 1.25,
                                p: 1,
                                borderRadius: 1,
                                borderLeft: "3px solid currentColor",
                                backgroundColor: isAgent
                                  ? "rgba(0, 0, 0, 0.05)"
                                  : "rgba(255, 255, 255, 0.16)",
                                opacity: 0.95,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  fontWeight: 700,
                                  mb: 0.25,
                                }}
                              >
                                {getSenderLabel(parentReply)}
                              </Typography>

                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {parentReply.COMMENT_TEXT ||
                                  parentReply.comment_text ||
                                  parentReply.attachment?.name ||
                                  "Attachment"}
                              </Typography>
                            </Box>
                          )}

                          {(reply.COMMENT_TEXT || reply.comment_text) && (
                            <Typography
                              variant="body2"
                              sx={{
                                whiteSpace: "pre-wrap",
                                overflowWrap: "anywhere",
                              }}
                            >
                              {reply.COMMENT_TEXT || reply.comment_text}
                            </Typography>
                          )}

                          {attachment && isImageAttachment(attachment) && (
                            <Box
                              component="a"
                              href={attachment.url}
                              target="_blank"
                              rel="noreferrer"
                              sx={{
                                display: "block",
                                mt:
                                  reply.COMMENT_TEXT || reply.comment_text
                                    ? 1.25
                                    : 0,
                                textDecoration: "none",
                              }}
                            >
                              <Box
                                component="img"
                                src={attachment.url}
                                alt={attachment.name || "Attachment"}
                                sx={{
                                  display: "block",
                                  width: "100%",
                                  maxWidth: 380,
                                  maxHeight: 340,
                                  objectFit: "cover",
                                  borderRadius: 1.5,
                                  border: "1px solid rgba(255,255,255,0.25)",
                                }}
                              />
                            </Box>
                          )}

                          {attachment && !isImageAttachment(attachment) && (
                            <Paper
                              component="a"
                              href={attachment.url}
                              target="_blank"
                              rel="noreferrer"
                              elevation={0}
                              sx={{
                                mt:
                                  reply.COMMENT_TEXT || reply.comment_text
                                    ? 1.25
                                    : 0,
                                p: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                color: isAgent
                                  ? "var(--text-primary)"
                                  : "#ffffff",
                                backgroundColor: isAgent
                                  ? "rgba(0, 0, 0, 0.05)"
                                  : "rgba(255, 255, 255, 0.15)",
                                border: "1px solid rgba(255,255,255,0.22)",
                                textDecoration: "none",
                                borderRadius: 1.5,
                              }}
                            >
                              <InsertDriveFileIcon fontSize="small" />

                              <Typography
                                variant="caption"
                                sx={{
                                  flex: 1,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {attachment.name ||
                                  attachment.fileName ||
                                  "Download attachment"}
                              </Typography>

                              <DownloadIcon fontSize="small" />
                            </Paper>
                          )}

                        </Paper>

                        <Stack
                          direction={isAgent ? "row" : "row-reverse"}
                          alignItems="center"
                          spacing={0.5}
                          sx={{
                            mt: 0.5,
                            px: 0.5,
                          }}
                        >
                          <Typography variant="caption" color="var(--text-secondary)">
                            {getSenderLabel(reply)} •{" "}
                            {getReplyDate(reply)
                              ? new Date(getReplyDate(reply)).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : "-"}
                          </Typography>
                          <Tooltip title="Reply to this message">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleSetReplyTo(reply)}
                                disabled={
                                  (activeTicket.ticket?.STATUS_NAME || activeTicket.status) ===
                                  "Resolved"
                                }
                                aria-label={`Reply to ${getSenderLabel(reply)}`}
                                sx={{
                                  width: 26,
                                  height: 26,
                                  color: "var(--text-secondary)",
                                  "& svg": { fontSize: 16 },
                                }}
                              >
                                <ReplyIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </Box>
                    );
                  },
                )}
                <Box ref={messagesEndRef} />
              </Stack>
            </Box>

            <Box
              component="form"
              onSubmit={handleSendReply}
              sx={{
                borderTop: "1px solid var(--surface-border)",
                backgroundColor: "var(--card-bg)",
              }}
            >
              {replyTo && (
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    backgroundColor: "var(--surface)",
                    borderBottom: "1px solid var(--surface-border)",
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="var(--text-primary)"
                    >
                      Replying to {getSenderLabel(replyTo)}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="var(--text-secondary)"
                      sx={{
                        display: "block",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {replyTo.COMMENT_TEXT ||
                        replyTo.comment_text ||
                        replyTo.attachment?.name ||
                        "Attachment"}
                    </Typography>
                  </Box>

                  <IconButton
                    size="small"
                    onClick={() => setReplyTo(null)}
                    aria-label="Cancel reply"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {file && (
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    backgroundColor: "var(--surface)",
                    borderBottom: "1px solid var(--surface-border)",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ minWidth: 0 }}
                  >
                    {filePreview ? (
                      <Box
                        component="img"
                        src={filePreview}
                        alt={file.name}
                        sx={{
                          width: 42,
                          height: 42,
                          objectFit: "cover",
                          borderRadius: 1,
                        }}
                      />
                    ) : (
                      <InsertDriveFileIcon color="action" />
                    )}

                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{
                          display: "block",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {file.name}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="var(--text-secondary)"
                      >
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                    </Box>
                  </Stack>

                  <IconButton
                    size="small"
                    onClick={clearSelectedFile}
                    aria-label="Remove selected file"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              <Box sx={{ px: { xs: 1.25, sm: 2 }, py: 1.5 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.txt"
                  onChange={handleFileChange}
                />

                <Stack direction="row" spacing={1} alignItems="flex-end">
                  <Tooltip title="Attach image or file">
                    <span>
                      <IconButton
                        onClick={() => fileInputRef.current?.click()}
                        disabled={
                          actionLoading ||
                          (activeTicket.ticket?.STATUS_NAME ||
                            activeTicket.status) === "Resolved"
                        }
                        aria-label="Attach file"
                        sx={{
                          width: 42,
                          height: 42,
                          border: "1px solid var(--surface-border)",
                          color: "var(--text-secondary)",
                        }}
                      >
                        <AttachFileIcon />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <TextField
                    id="ticket-reply-input"
                    placeholder={
                      (activeTicket.ticket?.STATUS_NAME ||
                        activeTicket.status) === "Resolved"
                        ? "This ticket is resolved."
                        : "Write your answer..."
                    }
                    size="small"
                    fullWidth
                    multiline
                    maxRows={4}
                    value={replyMessage}
                    onChange={(event) => setReplyMessage(event.target.value)}
                    onKeyDown={handleReplyKeyDown}
                    disabled={
                      actionLoading ||
                      (activeTicket.ticket?.STATUS_NAME ||
                        activeTicket.status) === "Resolved"
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        minHeight: 42,
                        borderRadius: "12px",
                        backgroundColor: "var(--surface)",
                      },
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={
                      actionLoading ||
                      (!replyMessage.trim() && !file) ||
                      (activeTicket.ticket?.STATUS_NAME ||
                        activeTicket.status) === "Resolved"
                    }
                    sx={{
                      backgroundColor: "var(--set-btn-bg)",
                      color: "var(--set-btn-text)",
                      borderRadius: "var(--set-btn-border-radius)",
                      minWidth: 44,
                      width: 44,
                      height: 42,
                      "&:hover": {
                        backgroundColor: "var(--primary-hover)",
                      },
                    }}
                  >
                    <SendIcon fontSize="small" />
                  </Button>
                </Stack>

                <Typography
                  variant="caption"
                  color="var(--text-secondary)"
                  sx={{ display: { xs: "none", sm: "block" }, mt: 0.75, ml: 6.5 }}
                >
                  Press Enter to send · Shift + Enter for a new line
                </Typography>

                {(activeTicket.ticket?.STATUS_NAME || activeTicket.status) ===
                  "Resolved" && (
                  <Typography
                    variant="caption"
                    color="var(--text-secondary)"
                    sx={{ display: "block", mt: 1 }}
                  >
                    This ticket is resolved and cannot receive new messages.
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        ) : (
          <DocumentsList
            data={tickets}
            loading={loading}
            error={listError}
            emptyTitle="No tickets found"
            onSelect={handleSelectTicket}
            page={page}
            onPageChange={(selectedPage) => setPage(selectedPage)}
            pageSize={pageSize}
            paginateLocally
            showSelection={false}
            actions={<ListButton variant="outline" onClick={() => setOpenCreateDialog(true)}><AddIcon fontSize="small" aria-hidden="true" />New Ticket</ListButton>}
          />
        )}
      </Stack>

      <Dialog
        open={openCreateDialog}
        onClose={() => {
          if (!actionLoading) setOpenCreateDialog(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "var(--set-btn-border-radius)",
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, fontWeight: 800 }}>
          Create Support Ticket
          <IconButton
            onClick={() => setOpenCreateDialog(false)}
            disabled={actionLoading}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <form onSubmit={handleCreateSubmit}>
          <DialogContent>
            <Stack spacing={2.5}>
              <TextField
                required
                fullWidth
                label="Ticket Title"
                placeholder="Brief summary of the issue"
                value={newTicket.title}
                onChange={(event) =>
                  setNewTicket({
                    ...newTicket,
                    title: event.target.value,
                  })
                }
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  select
                  fullWidth
                  label="Category"
                  value={newTicket.lt_ticket_category_id}
                  onChange={(event) =>
                    setNewTicket({
                      ...newTicket,
                      lt_ticket_category_id: event.target.value,
                    })
                  }
                  disabled={lookupLoading}
                >
                  {ticketCategories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  fullWidth
                  label="Priority"
                  value={newTicket.enum_ticket_priority_id}
                  onChange={(event) =>
                    setNewTicket({
                      ...newTicket,
                      enum_ticket_priority_id: event.target.value,
                    })
                  }
                  disabled={lookupLoading}
                >
                  {ticketPriorities.map((priority) => (
                    <MenuItem key={priority.id} value={priority.id}>
                      {priority.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              <TextField
                required
                fullWidth
                multiline
                rows={4}
                label="Description"
                placeholder="Explain the technical problem..."
                value={newTicket.description}
                onChange={(event) =>
                  setNewTicket({
                    ...newTicket,
                    description: event.target.value,
                  })
                }
              />

              <TextField
                fullWidth
                label="Remarks (Optional)"
                placeholder="Internal metadata or additional context..."
                value={newTicket.remarks}
                onChange={(event) =>
                  setNewTicket({
                    ...newTicket,
                    remarks: event.target.value,
                  })
                }
              />
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setOpenCreateDialog(false)}
              color="inherit"
              disabled={actionLoading}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={actionLoading || lookupLoading}
              sx={{
                backgroundColor: "var(--set-btn-bg)",
                color: "var(--set-btn-text)",
                borderRadius: "var(--set-btn-border-radius)",
                textTransform: "none",
                fontWeight: 700,
                px: 3,
                "&:hover": {
                  backgroundColor: "var(--primary-hover)",
                },
              }}
            >
              {actionLoading ? "Submitting..." : "Submit"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default Tickets;
